import React, { useEffect, useMemo, useState } from 'react';
import { ApiDrawer, getDrawers, addDrawer, updateDrawer, deleteDrawer, getUserGroups, ApiUserGroup, getDrawerPermissionsByDrawer, ApiPermission, addPermission, deletePermission } from '../api';
import { ApiPermissionTemplate, getPermissionTemplates, createPermissionTemplate } from '@api/permissionTemplates';
import { getFieldModels, ApiFieldModel } from '../api';

interface DrawerForm { drawerName: string; drawerDescription: string; folderFieldModelId: number | null; documentFieldModelId: number | null; }
const emptyDrawer: DrawerForm = { drawerName: '', drawerDescription: '', folderFieldModelId: null, documentFieldModelId: null };
const OBJECTTYPE_DRAWER = 1; // backend enum

interface PermState { [groupId: number]: { read: boolean; write: boolean; modify: boolean; delete: boolean; full: boolean; templateId?: number; permissionId?: string; }; }

export default function DrawerAdmin() {
  const [drawers, setDrawers] = useState<ApiDrawer[]>([]);
  const [groups, setGroups] = useState<ApiUserGroup[]>([]);
  const [templates, setTemplates] = useState<ApiPermissionTemplate[]>([]);
  const [fieldModels, setFieldModels] = useState<ApiFieldModel[]>([]);
  const [selectedDrawerId, setSelectedDrawerId] = useState<number | null>(null);
  const [drawerEditing, setDrawerEditing] = useState(false);
  const [drawerForm, setDrawerForm] = useState<DrawerForm>(emptyDrawer);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permState, setPermState] = useState<PermState>({});

  useEffect(() => { init(); }, []);

  // Sort groups alphabetically by name for consistent UI rendering
  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => a.groupName.localeCompare(b.groupName, undefined, { sensitivity: 'base' }));
  }, [groups]);

  async function init() {
    setLoading(true); setError(null);
    try { const [d, g, t, fm] = await Promise.all([getDrawers(), getUserGroups(), getPermissionTemplates(), getFieldModels()]); setDrawers(d); setGroups(g); setTemplates(t); setFieldModels(fm); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function loadPermissions(drawerId: number) {
    try {
      const perms = await getDrawerPermissionsByDrawer(drawerId); // assume returns list for drawer
      const map: PermState = {};
      perms.forEach(p => {
        const tmpl = templates.find(t => t.id === p.permissionTemplateId);
        map[p.groupId] = {
          read: !!(tmpl && (tmpl.canRead ?? (tmpl as any).CanRead)),
            write: !!(tmpl && (tmpl.canWrite ?? (tmpl as any).CanWrite)),
            modify: !!(tmpl && (tmpl.canModify ?? (tmpl as any).CanModify)),
            delete: !!(tmpl && (tmpl.canDelete ?? (tmpl as any).CanDelete)),
            full: !!(tmpl && (tmpl.fullControl ?? (tmpl as any).FullControl)),
            templateId: p.permissionTemplateId,
            permissionId: p.id
        };
      });
      // ensure every group present
      groups.forEach(g => { if (!map[g.id]) map[g.id] = { read: false, write: false, modify: false, delete: false, full: false }; });
      setPermState(map);
    } catch { setPermState({}); }
  }

  function handleSelectDrawer(id: number) {
    setSelectedDrawerId(id); setDrawerEditing(false);
    const d = drawers.find(dr => dr.id === id);
    setDrawerForm({ drawerName: d?.drawerName || '', drawerDescription: d?.drawerDescription || '', folderFieldModelId: (d as any)?.drawerFolderFieldModelId ?? (d as any)?.DrawerFolderFieldModelId ?? null, documentFieldModelId: (d as any)?.drawerDocumentFieldModelId ?? (d as any)?.DrawerDocumentFieldModelId ?? null });
    loadPermissions(id);
  }
  function handleAddDrawer() { setSelectedDrawerId(null); setDrawerEditing(true); setDrawerForm(emptyDrawer); setPermState({}); }
  function handleEditDrawer() { if (selectedDrawerId != null) setDrawerEditing(true); }

  async function handleDeleteDrawer() {
    if (selectedDrawerId == null) return; if (!window.confirm('Delete this drawer?')) return;
    setLoading(true); setError(null);
    try { await deleteDrawer(selectedDrawerId); await init(); setSelectedDrawerId(null); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function handleSaveDrawer() {
    setLoading(true); setError(null);
    try {
      const payload: any = { drawerName: drawerForm.drawerName, drawerDescription: drawerForm.drawerDescription };
      if (drawerForm.folderFieldModelId != null) { payload.drawerFolderFieldModelId = drawerForm.folderFieldModelId; payload.DrawerFolderFieldModelId = drawerForm.folderFieldModelId; }
      if (drawerForm.documentFieldModelId != null) { payload.drawerDocumentFieldModelId = drawerForm.documentFieldModelId; payload.DrawerDocumentFieldModelId = drawerForm.documentFieldModelId; }
      let id = selectedDrawerId;
      if (id == null) { const nd = await addDrawer(payload); id = nd.id; }
      else { await updateDrawer(id, payload); }
      await init(); setSelectedDrawerId(id!); setDrawerEditing(false); await loadPermissions(id!);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  function onDrawerFormChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    if (name === 'folderFieldModelId' || name === 'documentFieldModelId') setDrawerForm(p => ({ ...p, [name]: value ? Number(value) : null } as any));
    else setDrawerForm(p => ({ ...p, [name]: value }));
  }

  function ensureGroupState(prev: PermState, groupId: number) { if (!prev[groupId]) prev[groupId] = { read: false, write: false, modify: false, delete: false, full: false }; return prev[groupId]; }
  function togglePerm(groupId: number, key: keyof PermState[number]) { setPermState(prev => { const clone: PermState = { ...prev }; const gs = ensureGroupState(clone, groupId); (gs as any)[key] = !(gs as any)[key]; if (key !== 'full' && gs.full) gs.full = false; return clone; }); }
  function setFull(groupId: number) { setPermState(prev => { const clone: PermState = { ...prev }; const gs = ensureGroupState(clone, groupId); const newFull = !gs.full; gs.full = newFull; gs.read = newFull; gs.write = newFull; gs.modify = newFull; gs.delete = newFull; return clone; }); }

  async function ensureTemplate(read: boolean, write: boolean, modify: boolean, del: boolean, full: boolean): Promise<ApiPermissionTemplate> {
    // try find existing
    const found = templates.find(t => (t.fullControl ?? (t as any).FullControl) === full && (t.canRead ?? (t as any).CanRead) === read && (t.canWrite ?? (t as any).CanWrite) === write && (t.canModify ?? (t as any).CanModify) === modify && (t.canDelete ?? (t as any).CanDelete) === del);
    if (found) return found;
    // create
    const label = full ? 'Full Control' : ['R','W','M','D'].filter((flag, idx) => [read, write, modify, del][idx]).join('') || 'None';
    const tmpl = await createPermissionTemplate({ canRead: read, canWrite: write, canModify: modify, canDelete: del, fullControl: full, permissionLabel: label });
    setTemplates(prev => [...prev, tmpl]);
    return tmpl;
  }

  async function savePermissions() {
    if (selectedDrawerId == null) return;
    setLoading(true); setError(null);
    try {
      const currentPerms = await getDrawerPermissionsByDrawer(selectedDrawerId); // refresh current
      for (const g of sortedGroups) {
        const st = permState[g.id]; if (!st) continue;
        const hasAny = st.full || st.read || st.write || st.modify || st.delete;
        const existing = currentPerms.find(p => p.groupId === g.id && p.objectId === selectedDrawerId && p.objectTypeId === OBJECTTYPE_DRAWER);
        if (!hasAny && existing) { await deletePermission(existing.id); continue; }
        if (hasAny) {
          const tmpl = await ensureTemplate(st.read, st.write, st.modify, st.delete, st.full);
          if (!existing) {
            await addPermission({ groupId: g.id, objectId: selectedDrawerId, objectTypeId: OBJECTTYPE_DRAWER, permissionTemplateId: tmpl.id });
          } else if (existing.permissionTemplateId !== tmpl.id) {
            // simplest: delete and re-add (no update endpoint originally). If updatePermission available could use that.
            await deletePermission(existing.id);
            await addPermission({ groupId: g.id, objectId: selectedDrawerId, objectTypeId: OBJECTTYPE_DRAWER, permissionTemplateId: tmpl.id });
          }
        }
      }
      await loadPermissions(selectedDrawerId);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  const inputStyle: React.CSSProperties = { padding: '4px 6px', fontSize: 14, borderRadius: 4, border: '1px solid #cfd8dc', background: '#f7fafd', width: '100%' };
  const buttonPrimary: React.CSSProperties = { padding: '8px 16px', borderRadius: 6, background: '#4e6e8e', color: '#fff', fontWeight: 600, border: 'none', cursor: 'pointer' };
  const buttonSecondary: React.CSSProperties = { ...buttonPrimary, background: '#e0e6ed', color: '#123', fontWeight: 500 };
  const buttonDanger: React.CSSProperties = { ...buttonPrimary, background: '#c62828' };

  const folderModelName = fieldModels.find(fm => fm.id === drawerForm.folderFieldModelId)?.fieldModelName;
  const documentModelName = fieldModels.find(fm => fm.id === drawerForm.documentFieldModelId)?.fieldModelName;

  return (
    <div style={{ display: 'flex', height: 'calc(90vh / var(--ui-scale))', border: '1px solid #ccc', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ width: 380, borderRight: '1px solid #ccc', display: 'flex', flexDirection: 'column', background: '#f7fafd', minHeight: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e0e6ed' }}>
          <h3 style={{ margin: 0, fontWeight: 600 }}>Drawers</h3>
          <button onClick={handleAddDrawer} title='Add drawer' style={{ background: '#4e6e8e', border: 'none', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19' /><line x1='5' y1='12' x2='19' y2='12' /></svg>
          </button>
        </div>
        {loading && <div style={{ padding: '8px 16px' }}>Loading...</div>}
        {error && <div style={{ padding: '8px 16px', color: 'red' }}>{error}</div>}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#eaf1f7' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Name</th>
              </tr>
            </thead>
            <tbody>
              {drawers.map(d => {
                const sel = d.id === selectedDrawerId;
                return (
                  <tr key={d.id} onClick={() => handleSelectDrawer(d.id)} style={{ cursor: 'pointer', background: sel ? '#d8ecff' : 'transparent' }}>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7' }}>{d.drawerName}</td>
                  </tr>
                );
              })}
              {drawers.length === 0 && !loading && (<tr><td style={{ padding: 16, textAlign: 'center', color: '#888' }}>No drawers</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 24px', overflowY: 'auto', minHeight: 0 }}>
        {drawerEditing ? (
          <div style={{ maxWidth: 820 }}>
            <h3 style={{ marginTop: 0 }}>{selectedDrawerId == null ? 'Add Drawer' : 'Edit Drawer'}</h3>
            <form onSubmit={e => { e.preventDefault(); handleSaveDrawer(); }} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 160px 1fr', rowGap: 12, columnGap: 16, background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Name</label>
              <input name='drawerName' value={drawerForm.drawerName} onChange={onDrawerFormChange} required style={inputStyle} />
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Description</label>
              <input name='drawerDescription' value={drawerForm.drawerDescription} onChange={onDrawerFormChange} style={inputStyle} />
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Folder Field Model</label>
              <select name='folderFieldModelId' value={drawerForm.folderFieldModelId ?? ''} onChange={onDrawerFormChange} style={inputStyle}>
                <option value=''>-- select --</option>
                {fieldModels.map(fm => <option key={fm.id} value={fm.id}>{fm.fieldModelName}</option>)}
              </select>
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Document Field Model</label>
              <select name='documentFieldModelId' value={drawerForm.documentFieldModelId ?? ''} onChange={onDrawerFormChange} style={inputStyle}>
                <option value=''>-- select --</option>
                {fieldModels.map(fm => <option key={fm.id} value={fm.id}>{fm.fieldModelName}</option>)}
              </select>
              <div style={{ gridColumn: '1 / span 4', display: 'flex', gap: 12, marginTop: 8 }}>
                <button type='submit' style={buttonPrimary} disabled={loading}>Save</button>
                <button type='button' style={buttonSecondary} onClick={() => setDrawerEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedDrawerId != null ? (
          <div style={{ maxWidth: 1600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Drawer Details & Permissions</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleEditDrawer} style={buttonSecondary} disabled={loading}>Edit</button>
                <button onClick={handleDeleteDrawer} style={buttonDanger} disabled={loading}>Delete</button>
              </div>
            </div>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20, display: 'grid', gridTemplateColumns: '200px 1fr', rowGap: 6 }}>
              <div style={{ fontWeight: 500 }}>Name:</div><div>{drawerForm.drawerName}</div>
              <div style={{ fontWeight: 500 }}>Description:</div><div>{drawerForm.drawerDescription}</div>
              <div style={{ fontWeight: 500 }}>Folder Field Model:</div><div>{folderModelName || '(none)'}</div>
              <div style={{ fontWeight: 500 }}>Document Field Model:</div><div>{documentModelName || '(none)'}</div>
            </div>

            <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Permissions per Group</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', minWidth: 900, fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#eaf1f7' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Group</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Read</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Write</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Modify</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Delete</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedGroups.map(g => {
                      const st = permState[g.id] || { read: false, write: false, modify: false, delete: false, full: false };
                      return (
                        <tr key={g.id} style={{ background: st.full ? '#fff9e0' : 'transparent' }}>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7', textAlign: 'left' }}>{g.groupName}</td>
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.read} onChange={() => togglePerm(g.id, 'read')} disabled={st.full} /></td>
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.write} onChange={() => togglePerm(g.id, 'write')} disabled={st.full} /></td>
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.modify} onChange={() => togglePerm(g.id, 'modify')} disabled={st.full} /></td>
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.delete} onChange={() => togglePerm(g.id, 'delete')} disabled={st.full} /></td>
                          <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.full} onChange={() => setFull(g.id)} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
                <button onClick={savePermissions} style={buttonPrimary} disabled={loading}>Save Permissions</button>
                <button onClick={() => loadPermissions(selectedDrawerId!)} style={buttonSecondary} disabled={loading}>Reset</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#567', fontSize: 16, paddingTop: 12 }}>Select a drawer or click the + icon to add a new drawer.</div>
        )}
      </div>
    </div>
  );
}
